<template>
  <div class="follow-page" :class="{ embedded: embedded }">
    <div class="container">
      <h2 v-if="!$props.embedded" class="page-title">我的关注</h2>

      <!-- Tab 切换 -->
      <div class="tab-bar">
        <div
          class="tab-item"
          :class="{ active: currentTab === 'follows' }"
          @click="switchTab('follows')"
        >
          关注列表
          <span class="tab-count">{{ countData.following }}</span>
        </div>
        <div
          class="tab-item"
          :class="{ active: currentTab === 'fans' }"
          @click="switchTab('fans')"
        >
          粉丝列表
          <span class="tab-count">{{ countData.fans }}</span>
        </div>
      </div>

      <!-- 统计概览 -->
      <div class="stats-row">
        <div class="stat-card">
          <span class="stat-num">{{ countData.following }}</span>
          <span class="stat-lbl">关注</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-card">
          <span class="stat-num">{{ countData.fans }}</span>
          <span class="stat-lbl">粉丝</span>
        </div>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading">加载中...</div>

      <!-- 列表 -->
      <template v-else>
        <div v-if="list.length === 0" class="empty-tip">
          暂无{{ currentTab === 'follows' ? '关注' : '粉丝' }}
        </div>

        <div v-else class="user-list">
          <div
            v-for="item in list"
            :key="item.id"
            class="user-card"
            @click="goUserPage(item.id)"
          >
            <img
              :src="item.avatar || defaultAvatar"
              class="user-avatar"
              alt="头像"
            >
            <div class="user-info">
              <div class="user-name-row">
                <span class="user-name">{{ item.nickname || item.username }}</span>
                <span
                  v-if="item.is_following === 2"
                  class="tag mutual"
                >互相关注</span>
                <span
                  v-else-if="item.is_following === 1"
                  class="tag followed"
                >已关注</span>
              </div>
              <p class="user-bio">{{ item.bio || '这个人很懒，什么都没写~' }}</p>
            </div>
            <button
              v-if="currentTab === 'fans' || (currentTab === 'follows' && !item.is_self)"
              class="follow-btn"
              :class="{ followed: item.is_following > 0 }"
              @click.stop="toggleFollow(item)"
            >
              {{ item.is_following > 0 ? '已关注' : '关注' }}
            </button>
          </div>
        </div>

        <!-- 加载更多 -->
        <div
          v-if="hasMore"
          class="load-more"
          @click="loadMore"
        >
          加载更多
        </div>
        <div
          v-else-if="list.length > 0"
          class="load-end"
        >
          没有更多了
        </div>
      </template>
    </div>
    <PageFooter v-if="!$props.embedded" />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import PageFooter from './Footer.vue';
import { getFollowList, getFansList, getFollowCount, follow, unfollow } from '../api/follow';

export default {
  name: 'FollowList',
  components: { PageFooter },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    void props.embedded;
    const currentTab = ref('follows');
    const list = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const hasMore = ref(true);
    const countData = ref({ following: 0, fans: 0 });
    const defaultAvatar = require('../assets/logo.png');

    const fetchList = async (pageNum = 1, append = false) => {
      loading.value = true;
      try {
        const apiFn = currentTab.value === 'follows' ? getFollowList : getFansList;
        const res = await apiFn({ page: pageNum });
        if (res.code === 1) {
          const data = res.data;
          const items = data.list || data.data || [];
          if (append) {
            list.value = [...list.value, ...items];
          } else {
            list.value = items;
          }
          hasMore.value = items.length >= (data.per_page || 10);
          page.value = pageNum;
        }
      } catch (e) {
        console.error('获取列表失败', e);
      } finally {
        loading.value = false;
      }
    };

    const fetchCount = async () => {
      try {
        const res = await getFollowCount();
        if (res.code === 1) {
          countData.value = res.data;
        }
      } catch (e) {
        console.error('获取统计数据失败', e);
      }
    };

    const switchTab = (tab) => {
      if (currentTab.value === tab) return;
      currentTab.value = tab;
      page.value = 1;
      hasMore.value = true;
      fetchList();
    };

    const loadMore = () => {
      if (!hasMore.value || loading.value) return;
      fetchList(page.value + 1, true);
    };

    const toggleFollow = async (item) => {
      try {
        const targetId = item.follow_user_id || item.user_id || item.id;
        const wasFollowing = item.is_following > 0;
        const res = wasFollowing
          ? await unfollow(targetId)
          : await follow(targetId);
        if (res.code === 1) {
          item.is_following = wasFollowing ? 0 : 1;
          fetchCount();
        } else {
          alert(res.msg || '操作失败');
        }
      } catch (e) {
        alert('操作失败，请重试');
      }
    };

    const goUserPage = (userId) => {
      // 跳转到用户主页，可以改成 router.push
      window.location.hash = '#/user/' + userId;
    };

    onMounted(() => {
      fetchList();
      fetchCount();
    });

    return {
      currentTab,
      list,
      loading,
      hasMore,
      countData,
      defaultAvatar,
      switchTab,
      loadMore,
      toggleFollow,
      goUserPage
    };
  }
};
</script>

<style scoped>
.follow-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f6f7f9;
  padding: 40px 0;
}
.follow-page.embedded {
  min-height: auto;
  padding: 0;
  background: transparent;
}
.follow-page.embedded .container {
  max-width: none;
  margin: 0;
  padding: 0;
}
.container {
  flex: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 20px;
}
.page-title {
  font-size: 24px;
  color: #1a1a2e;
  margin-bottom: 24px;
  font-weight: 700;
  position: relative;
  padding-left: 16px;
}
.page-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background: linear-gradient(180deg, #ff6a00, #ff9600);
  border-radius: 2px;
}

/* Tab 切换 */
.tab-bar {
  display: flex;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.tab-item {
  flex: 1;
  text-align: center;
  padding: 14px 0;
  font-size: 15px;
  font-weight: 500;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}
.tab-item:hover {
  color: #ff9600;
  background: #fffaf5;
}
.tab-item.active {
  color: #ff9600;
  background: #fff;
}
.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: linear-gradient(90deg, #ff9600, #ff7d00);
  border-radius: 2px;
}
.tab-count {
  display: inline-block;
  margin-left: 6px;
  font-size: 12px;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 0 6px;
  border-radius: 8px;
  line-height: 18px;
}
.tab-item.active .tab-count {
  background: #fff0e0;
  color: #ff9600;
}

/* 统计概览 */
.stats-row {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.stat-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;
}
.stat-num {
  font-size: 26px;
  font-weight: 700;
  color: #1a1a2e;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.stat-lbl {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 4px;
}
.stat-divider {
  width: 1px;
  height: 40px;
  background: #e2e8f0;
}

/* 用户列表 */
.user-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.user-card {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.user-card:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
  transform: translateY(-2px);
  border-color: rgba(255,150,0,0.15);
}
.user-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  margin-right: 14px;
  border: 2px solid #f0f0f0;
  transition: border-color 0.3s;
}
.user-card:hover .user-avatar {
  border-color: #ff9600;
}
.user-info {
  flex: 1;
  min-width: 0;
}
.user-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.user-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
}
.tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  flex-shrink: 0;
  font-weight: 500;
}
.tag.mutual {
  background: linear-gradient(135deg, #fff0e0, #fff5e6);
  color: #ff9600;
  border: 1px solid rgba(255,150,0,0.15);
}
.tag.followed {
  background: #f1f5f9;
  color: #94a3b8;
}
.user-bio {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 关注按钮 */
.follow-btn {
  flex-shrink: 0;
  padding: 7px 18px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 6px rgba(255,150,0,0.2);
}
.follow-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,150,0,0.3);
}
.follow-btn.followed {
  background: #f1f5f9;
  color: #94a3b8;
  box-shadow: none;
}
.follow-btn.followed:hover {
  background: #fef2f2;
  color: #ef4444;
  box-shadow: none;
}

/* 加载更多 */
.load-more {
  text-align: center;
  padding: 16px;
  color: #ff9600;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;
}
.load-more:hover {
  color: #ff7d00;
  transform: translateY(-1px);
}
.load-end {
  text-align: center;
  padding: 16px;
  color: #cbd5e1;
  font-size: 13px;
}

.empty-tip {
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
  font-size: 15px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.02);
}

.loading {
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
  font-size: 15px;
}
</style>
